import { logUnhandledError, logPromiseRejection } from './errorLogger';

// Frames from our own bundle. If any appear, the error is ours to fix.
const OUR_CODE_MARKERS = ['/static/js/', '/static/media/'];

/**
 * Whether an error demonstrably originated outside our bundle.
 *
 * Deliberately conservative: it requires positive evidence of a foreign
 * origin, so anything ambiguous still gets reported. Frames at <anonymous>
 * come from code a third-party tag evaluates at runtime.
 */
function isThirdPartyFrame(stack?: string, source?: string): boolean {
  const frames = `${stack || ''}\n${source || ''}`.trim();

  // No evidence either way — report it.
  if (!frames) return false;

  // Anything touching our bundle is ours.
  if (OUR_CODE_MARKERS.some(marker => frames.includes(marker))) return false;

  if (frames.includes('<anonymous>')) return true;

  const urls = frames.match(/https?:\/\/[^\s)]+/g) || [];
  return urls.length > 0 && urls.every(url => !url.startsWith(window.location.origin));
}

// Setup global error handlers
export const setupGlobalErrorHandlers = (): void => {
  // Handle uncaught errors
  window.onerror = (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error
  ): boolean => {
    const errorMessage = typeof message === 'string' ? message : 'Unknown error';

    // Don't log errors from browser extensions or external scripts
    if (source && (source.includes('chrome-extension') || source.includes('moz-extension'))) {
      return false;
    }

    // A cross-origin script threw and the browser masked it: the message is
    // exactly "Script error.", source is empty, line/column are 0 and there is
    // no Error object. Nothing to diagnose, nothing here to fix. The extension
    // check above misses these precisely because their source is blank.
    if ((errorMessage === 'Script error.' || errorMessage === 'Script error') && !error) {
      console.debug('[ErrorHandlers] Ignored opaque cross-origin script error');
      return false;
    }

    // Errors thrown inside Google's advertising and analytics tags reach us
    // with a full stack because gtag is loaded with crossorigin="anonymous",
    // which lifts that masking. Their frames sit at <anonymous> — code the tag
    // evaluates at runtime — or at a third-party origin, and never touch our
    // bundle. We can't fix code we don't ship, so log it for the console and
    // move on rather than raising an alert.
    if (isThirdPartyFrame(error?.stack, source)) {
      console.debug('[ErrorHandlers] Ignored third-party script error:', errorMessage);
      return false;
    }

    logUnhandledError(errorMessage, source, lineno, colno, error);

    // Return false to let the default handler run as well
    return false;
  };

  // Handle unhandled promise rejections
  window.onunhandledrejection = (event: PromiseRejectionEvent): void => {
    logPromiseRejection(event.reason);
  };

  console.log('[ErrorHandlers] Global error handlers initialized');
};

// Remove global error handlers (for cleanup)
export const removeGlobalErrorHandlers = (): void => {
  window.onerror = null;
  window.onunhandledrejection = null;
};
